import {
  type AppError,
  BadRequestError,
  ForbiddenError,
  InvalidTokenError,
  NetworkError,
  NotFoundError,
  type ResultWithOauthToken,
} from '@packages/utils';
import {type UseQueryOptions, useQuery} from '@tanstack/react-query';
import {parseCookies} from 'nookies';
import {z} from 'zod';
import {DEFAULT_API_VERSION} from '../../config/apiVersion';
import {defaultClient} from '../../lib/defaultClient';
import {createPathWithQuery} from '../../shared/services/createPathWithQuery';
import {filterExistParams} from '../../shared/services/filterExistParams';
import {
  RECRUITMENT_ORDER,
  RECRUITMENT_TAGS,
} from '../entities/recruitment/constants';
import {
  type GetEntrySimilarRecruitmentListResponse,
  GetEntrySimilarRecruitmentListResponseScheme,
  type GetRecommendRecruitmentListResponse,
  GetRecommendRecruitmentListResponseSchema,
  type GetRecommendedRecruitmentListResponse,
  GetRecommendedRecruitmentListResponseSchema,
  type GetRecommendedRecruitsByIndustryResponse,
  GetRecommendedRecruitsByIndustrySchema,
  GetRecruitmentIdByDetailIdResponseSchema,
  type GetRecruitmentListResponse,
  type GetRecruitmentWebSeminarsResponse,
  GetRecruitmentWebSeminarsResponseSchema,
  type GetSimilarRecruitmentListResponse,
  GetSimilarRecruitmentListResponseScheme,
  type InternalPreviewCodeCurrentUnivType,
  type RecruitmentDetailResponse,
  RecruitmentDetailResponseSchema,
  RecruitmentListResponseSchema,
  type RelatedCompanyRecruitmentListResponse,
  RelatedCompanyRecruitmentListResponseSchema,
} from '../entities/recruitment/schema';
import {sendApiRequest} from '../usecases/auth/sendApiRequest';
import {useIsSessionExpired} from '../usecases/auth/useIsSessionExpired';

/**
 * GetRecruitmentItemIdByDetailId
 * `gs_recruiting_items.detail_id`とadmin/clientの値から`gs_recruiting_items.id`を取得する
 */
type getRecruitmentIdByDetailIdParam = {
  cookies: Partial<Record<string, string>>;
  presenter: 'admin' | 'client';
  detailId: number;
  version?: string;
};

export const getRecruitmentIdByDetailId = async ({
  cookies,
  presenter,
  detailId,
  version = DEFAULT_API_VERSION,
}: getRecruitmentIdByDetailIdParam) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    return defaultClient.get(
      `/${version}/recruitment-detail/${presenter}/${detailId}`,
      GetRecruitmentIdByDetailIdResponseSchema,
    );
  };

  const result = await sendApiRequest({
    request,
    authRequired: false,
    cookies,
    version,
    allowReturnError: true,
  });

  return result;
};

/**
 * GetRecruitmentList
 */
export const GetRecruitmentOrderType = z.union([
  z.literal(RECRUITMENT_ORDER.RECOMMEND),
  z.literal(RECRUITMENT_ORDER.RECENT),
  z.literal(RECRUITMENT_ORDER.DEADLINE),
  z.literal(RECRUITMENT_ORDER.EVENT),
]);
export type GetRecruitmentOrderType = z.infer<typeof GetRecruitmentOrderType>;

export const GetRecruitmentTagsType = z.union([
  z.literal(RECRUITMENT_TAGS.OVERSEAS_STUDENT),
  z.literal(RECRUITMENT_TAGS.SCIENCE_STUDENT),
  z.literal(RECRUITMENT_TAGS.ENGINEER),
]);
export type GetRecruitmentTagsType = z.infer<typeof GetRecruitmentTagsType>;

type GetRecruitmentListParam = {
  order?: GetRecruitmentOrderType;
  companyIDs?: number[];
  industryCategoryIDs?: number[];
  groupedJobTypeIDs?: number[];
  gy?: number;
  allgy?: 'true' | 'false';
  typeIDs?: string[];
  tagIDs?: GetRecruitmentTagsType[];
  page?: number;
  limit?: number;
  isShuffle?: 'true' | 'false';
};
export type IGetRecruitmentList = GetRecruitmentListParam & {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export type GetRecruitmentListReturnValueType = ResultWithOauthToken<
  GetRecruitmentListResponse,
  AppError
>;

export const getRecruitmentList = async ({
  cookies,
  order,
  companyIDs,
  industryCategoryIDs,
  groupedJobTypeIDs,
  gy,
  allgy,
  typeIDs,
  tagIDs,
  page,
  limit,
  isShuffle,
  version = DEFAULT_API_VERSION,
}: IGetRecruitmentList) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/recruitment`,
      {
        order: order ?? RECRUITMENT_ORDER.RECENT,
        companyIDs: companyIDs ?? '',
        industryCategoryIDs: industryCategoryIDs ?? '',
        groupedJobTypeIDs: groupedJobTypeIDs ?? '',
        gy,
        allgy: allgy ?? 'false',
        typeIDs: typeIDs ?? '',
        tagIDs: tagIDs ?? '',
        page: page ?? 1,
        limit: limit ?? 10,
        isShuffle: isShuffle ?? 'false',
      },
      'none',
    );
    return defaultClient.get(path, RecruitmentListResponseSchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type GetRecruitmentListOptions = {
  config?: UseQueryOptions<GetRecruitmentListReturnValueType>;
} & Omit<IGetRecruitmentList, 'cookies'>;

export const useGetRecruitmentList = ({
  order,
  companyIDs,
  industryCategoryIDs,
  groupedJobTypeIDs,
  gy,
  allgy,
  typeIDs,
  tagIDs,
  page,
  limit,
  isShuffle,
  config,
}: GetRecruitmentListOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetRecruitmentListReturnValueType>({
    ...config,
    queryKey: [
      'getRecruitmentList',
      order,
      companyIDs,
      industryCategoryIDs,
      groupedJobTypeIDs,
      gy,
      allgy,
      typeIDs,
      tagIDs,
      page,
      limit,
      isShuffle,
    ],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getRecruitmentList({
        cookies,
        order,
        companyIDs,
        industryCategoryIDs,
        groupedJobTypeIDs,
        gy,
        allgy,
        typeIDs,
        tagIDs,
        page,
        limit,
        isShuffle,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetRecruitmentDetail
 * GET /recruitment/${recruitmentID}/web
 */
type GetRecruitmentDetailArgs = {
  cookies: Partial<Record<string, string>>;
  recruitmentID: number;
  preview_code?: string;
  internal_preview_code?: string;
  sex?: number;
  bun_ri?: number;
  university?: number;
  current_univ_type?: InternalPreviewCodeCurrentUnivType;
  version?: string;
};

export type GetRecruitmentDetailReturnValueType = ResultWithOauthToken<
  RecruitmentDetailResponse,
  AppError
>;

export const getRecruitmentDetail = async ({
  cookies,
  recruitmentID,
  preview_code,
  internal_preview_code,
  sex,
  bun_ri,
  university,
  current_univ_type,
  version = DEFAULT_API_VERSION,
}: GetRecruitmentDetailArgs): Promise<GetRecruitmentDetailReturnValueType> => {
  const request = async (
    accessToken: string,
  ): Promise<GetRecruitmentDetailReturnValueType> => {
    defaultClient.setToken(accessToken);

    const params: {
      preview_code?: string;
      internal_preview_code?: string;
      sex?: number;
      bun_ri?: number;
      university?: number;
      current_univ_type?: string;
    } = filterExistParams({
      preview_code,
      internal_preview_code,
      sex,
      bun_ri,
      university,
      current_univ_type,
    });

    if (
      accessToken &&
      Object.hasOwn(params, 'preview_code') &&
      params.preview_code
    ) {
      // エラー画面表示のため、preview_codeの指定があってログイン状態の場合はForbiddenErrorとして返す、throwはしない
      return {
        ok: false,
        error: ForbiddenError,
      };
    }

    const path = createPathWithQuery(
      `/${version}/recruitment/${recruitmentID}/web`,
      params,
    );

    return defaultClient.get(path, RecruitmentDetailResponseSchema);
  };

  try {
    const result = await sendApiRequest({
      request,
      cookies,
      authRequired: false,
      version,
    });

    return result;
  } catch (error: any) {
    if (preview_code) {
      // エラー画面表示のため、preview_codeの指定があってエラーの場合はすべてBadRequestErrorとして返す、throwはしない
      return {
        ok: false,
        error: BadRequestError,
      };
    }

    if (error === NotFoundError) {
      return {
        ok: false,
        error: NotFoundError,
      };
    }

    return {
      ok: false,
      error: NetworkError,
    };
  }
};

/**
 * GetRecruitmentWebSeminars
 * v3.0
 */
type GetRecruitmentWebSeminarsArgs = {
  cookies: Partial<Record<string, string>>;
  companyId: number;
  limit: number;
  version?: string;
};

type GetRecruitmentWebSeminarsReturnValueType = ResultWithOauthToken<
  GetRecruitmentWebSeminarsResponse,
  AppError
>;

export const getRecruitmentWebSeminars = async ({
  cookies,
  companyId,
  limit,
  version = DEFAULT_API_VERSION,
}: GetRecruitmentWebSeminarsArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/recruitment/web-seminars/${companyId}`,
      {limit},
    );

    return defaultClient.get(path, GetRecruitmentWebSeminarsResponseSchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetRecruitmentWebSeminarsParams = Omit<
  GetRecruitmentWebSeminarsArgs,
  'cookies'
> & {
  config?: UseQueryOptions<GetRecruitmentWebSeminarsReturnValueType>;
};

export const useGetRecruitmentWebSeminars = ({
  companyId,
  limit,
  version,
  config,
}: UseGetRecruitmentWebSeminarsParams) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetRecruitmentWebSeminarsReturnValueType>({
    ...config,
    queryKey: ['getRecruitmentWebSeminars', companyId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getRecruitmentWebSeminars({
        cookies,
        companyId,
        limit,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetSimilarRecruitmentList
 */
type GetSimilarRecruitmentListArgs = {
  cookies: Partial<Record<string, string>>;
  recruitmentDetailId: number; // 募集情報詳細URL末尾のID
  numberOfMatches?: number;
  numberOfRandom?: number;
  allowDuplicateCompany?: boolean;
  version?: string;
};

type GetSimilarRecruitmentListReturnValueType = ResultWithOauthToken<
  GetSimilarRecruitmentListResponse,
  AppError
>;

export const GetSimilarRecruitmentList = async ({
  cookies,
  recruitmentDetailId,
  version = DEFAULT_API_VERSION,
}: GetSimilarRecruitmentListArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/recruitment/${recruitmentDetailId}/similarities`,
      {},
    );

    return defaultClient.get(path, GetSimilarRecruitmentListResponseScheme);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetSimilarRecruitmentListParams = Omit<
  GetSimilarRecruitmentListArgs,
  'cookies'
> & {
  config?: UseQueryOptions<GetSimilarRecruitmentListReturnValueType>;
};

export const useGetSimilarRecruitmentList = ({
  recruitmentDetailId,
  version,
  config,
}: UseGetSimilarRecruitmentListParams) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetSimilarRecruitmentListReturnValueType>({
    ...config,
    queryKey: ['getSimilarRecruitmentList', recruitmentDetailId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await GetSimilarRecruitmentList({
        cookies,
        recruitmentDetailId,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetRecommendedRecruitmentList
 */
type GetRecommendedRecruitmentListArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
};

type GetRecommendedRecruitmentListReturnValueType = ResultWithOauthToken<
  GetRecommendedRecruitmentListResponse,
  AppError
>;

export const GetRecommendedRecruitmentList = async ({
  cookies,
  version = DEFAULT_API_VERSION,
}: GetRecommendedRecruitmentListArgs): Promise<GetRecommendedRecruitmentListReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    // エンドポイントは未ログインを許容するが仕様と異なるため、accessTokenは必須とする

    return defaultClient.get(
      `/${version}/recruitment/recommends`,
      GetRecommendedRecruitmentListResponseSchema,
    );
  };

  const result = await sendApiRequest({
    request,
    cookies,
    version,
  });

  return result;
};

type UseGetRecommendedRecruitmentListParams = {
  version?: string;
  config?: UseQueryOptions<GetRecommendedRecruitmentListReturnValueType>;
};

export const useGetRecommendedRecruitmentList = ({
  version,
  config,
}: UseGetRecommendedRecruitmentListParams) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetRecommendedRecruitmentListReturnValueType>({
    ...config,
    queryKey: ['getRecommendedRecruitmentList'],
    async queryFn() {
      const cookies = parseCookies();
      const result = await GetRecommendedRecruitmentList({
        cookies,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetRelatedCompanyRecruitmentList
 * エンドポイント修正中のためresponseShapeErrorになります
 */

type GetRelatedCompanyRecruitmentListArgs = {
  cookies: Partial<Record<string, string>>;
  companyID: number;
  version?: string;
};

type GetRelatedCompanyRecruitmentListReturnValueType = ResultWithOauthToken<
  RelatedCompanyRecruitmentListResponse,
  AppError
>;

export const GetRelatedCompanyRecruitmentList = async ({
  cookies,
  companyID,
  version = DEFAULT_API_VERSION,
}: GetRelatedCompanyRecruitmentListArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/recruitment/related-companies/${companyID}`,
      {},
    );

    return defaultClient.get(path, RelatedCompanyRecruitmentListResponseSchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetRelatedCompanyRecruitmentListParams = Omit<
  GetRelatedCompanyRecruitmentListArgs,
  'cookies'
> & {
  config?: UseQueryOptions<GetRelatedCompanyRecruitmentListReturnValueType>;
};

export const useGetRelatedCompanyRecruitmentList = ({
  companyID,
  version,
  config,
}: UseGetRelatedCompanyRecruitmentListParams) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetRelatedCompanyRecruitmentListReturnValueType>({
    ...config,
    queryKey: ['getRelatedCompanyRecruitmentList', companyID],
    async queryFn() {
      const cookies = parseCookies();
      const result = await GetRelatedCompanyRecruitmentList({
        cookies,
        companyID,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

type ResultGetRecommendedRecruitsByIndustry = ResultWithOauthToken<
  GetRecommendedRecruitsByIndustryResponse,
  AppError
>;

type GetRecommendedRecruitsByIndustryArgs = {
  industryId: number;
  cookies: Partial<Record<string, string>>;
  version?: string;
};

export const getRecommendedRecruitsByIndustry = async ({
  industryId,
  cookies,
  version = DEFAULT_API_VERSION,
}: GetRecommendedRecruitsByIndustryArgs): Promise<ResultGetRecommendedRecruitsByIndustry> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = `/${version}/industries/${industryId}/recommended-recruitments`;
    return defaultClient.get(path, GetRecommendedRecruitsByIndustrySchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    version,
  });

  return result;
};

type UseGetRecommendedRecruitsByIndustryOptions = {
  config?: UseQueryOptions<ResultGetRecommendedRecruitsByIndustry>;
} & Omit<GetRecommendedRecruitsByIndustryArgs, 'cookies'>;

export const useGetRecommendedRecruitsByIndustry = ({
  industryId,
  version = DEFAULT_API_VERSION,
  config,
}: UseGetRecommendedRecruitsByIndustryOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();

  return useQuery<ResultGetRecommendedRecruitsByIndustry>({
    ...config,
    queryKey: ['getRecommendedRecruitsByIndustry', industryId],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getRecommendedRecruitsByIndustry({
        industryId,
        cookies,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetEntrySimilarRecruitmentList
 * users/me/entries/:entryID/recruitment-similarities
 * アーカイブ募集の/similaritiesとは違う
 */
type GetEntrySimilarRecruitmentListArgs = {
  cookies: Partial<Record<string, string>>;
  entryID: number; // エントリーID
  numberOfMatches?: number;
  numberOfRandom?: number;
  allowDuplicateCompany?: boolean;
  version?: string;
};

type GetEntrySimilarRecruitmentListReturnValueType = ResultWithOauthToken<
  GetEntrySimilarRecruitmentListResponse,
  AppError
>;

export const GetEntrySimilarRecruitmentList = async ({
  cookies,
  entryID,
  version = DEFAULT_API_VERSION,
}: GetEntrySimilarRecruitmentListArgs) => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);

    const path = createPathWithQuery(
      `/${version}/users/me/entries/${entryID}/recruitment-similarities`,
      {},
    );

    return defaultClient.get(
      path,
      GetEntrySimilarRecruitmentListResponseScheme,
    );
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: false,
    version,
  });

  return result;
};

type UseGetEntrySimilarRecruitmentListParams = Omit<
  GetEntrySimilarRecruitmentListArgs,
  'cookies'
> & {
  config?: UseQueryOptions<GetEntrySimilarRecruitmentListReturnValueType>;
};

export const useGetEntrySimilarRecruitmentList = ({
  entryID,
  version,
  config,
}: UseGetEntrySimilarRecruitmentListParams) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetEntrySimilarRecruitmentListReturnValueType>({
    ...config,
    queryKey: ['getEntrySimilarRecruitmentList', entryID],
    async queryFn() {
      const cookies = parseCookies();
      const result = await GetEntrySimilarRecruitmentList({
        cookies,
        entryID,
        version,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};

/**
 * GetRecommendRecruitmentList
 * v3.0/feed/recommend-recruitment
 */
type GetRecommendRecruitmentListArgs = {
  cookies: Partial<Record<string, string>>;
  version?: string;
  limit?: number;
};

type GetRecommendRecruitmentListReturnValueType = ResultWithOauthToken<
  GetRecommendRecruitmentListResponse,
  AppError
>;

export const getRecommendRecruitmentList = async ({
  cookies,
  version = DEFAULT_API_VERSION,
  limit,
}: GetRecommendRecruitmentListArgs): Promise<GetRecommendRecruitmentListReturnValueType> => {
  const request = async (accessToken: string) => {
    defaultClient.setToken(accessToken);
    const path = createPathWithQuery(`/${version}/feed/recommend-recruitment`, {
      limit,
    });
    return defaultClient.get(path, GetRecommendRecruitmentListResponseSchema);
  };

  const result = await sendApiRequest({
    request,
    cookies,
    authRequired: true,
    version,
    allowReturnError: true,
  });

  return result;
};

type UseRecommendRecruitmentListOptions = {
  config?: UseQueryOptions<GetRecommendRecruitmentListReturnValueType>;
} & Omit<GetRecommendRecruitmentListArgs, 'cookies'>;

export const useRecommendRecruitmentList = ({
  version = DEFAULT_API_VERSION,
  limit,
  config,
}: UseRecommendRecruitmentListOptions) => {
  const {updateIsSessionExpired} = useIsSessionExpired();
  return useQuery<GetRecommendRecruitmentListReturnValueType>({
    ...config,
    queryKey: ['getRecommendRecruitmentList', version, limit],
    async queryFn() {
      const cookies = parseCookies();
      const result = await getRecommendRecruitmentList({
        cookies,
        version,
        limit,
      });
      if (!result.ok && result.error === InvalidTokenError) {
        updateIsSessionExpired(true);
      }

      return result;
    },
  });
};
